import { useRef, useEffect, useCallback, useState } from 'react';

interface ColorSpectrumProps {
  onColorSelect: (color: string) => void;
  width?: number;
  height?: number;
}

function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

export function ColorSpectrum({
  onColorSelect,
  width = 32,
  height = 320,
}: ColorSpectrumProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDraggingRef = useRef(false);
  const [indicatorPos, setIndicatorPos] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const imageData = ctx.createImageData(width * dpr, height * dpr);
    const data = imageData.data;
    const w = width * dpr;
    const h = height * dpr;

    Array.from({ length: h }).forEach((_, py) => {
      const hue = (py / h) * 360;
      Array.from({ length: w }).forEach((_, px) => {
        const lightness = 15 + (px / w) * 70;
        const s = 1;
        const l = lightness / 100;
        const a = s * Math.min(l, 1 - l);
        const f = (n: number) => {
          const k = (n + hue / 30) % 12;
          return l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        };
        const idx = (py * w + px) * 4;
        data[idx] = Math.round(f(0) * 255);
        data[idx + 1] = Math.round(f(8) * 255);
        data[idx + 2] = Math.round(f(4) * 255);
        data[idx + 3] = 255;
      });
    });

    ctx.putImageData(imageData, 0, 0);
  }, [width, height]);

  const pickColor = useCallback(
    (e: React.PointerEvent) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const x = Math.max(0, Math.min(width - 1, e.clientX - rect.left));
      const y = Math.max(0, Math.min(height - 1, e.clientY - rect.top));
      setIndicatorPos({ x, y });
      onColorSelect(hslToHex((y / height) * 360, 100, 15 + (x / width) * 70));
    },
    [width, height, onColorSelect],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      isDraggingRef.current = true;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      pickColor(e);
    },
    [pickColor],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDraggingRef.current) return;
      pickColor(e);
    },
    [pickColor],
  );

  const handlePointerUp = useCallback(() => {
    isDraggingRef.current = false;
  }, []);

  return (
    <div className="color-spectrum-wrapper" style={{ width, height }}>
      <canvas
        ref={canvasRef}
        style={{ width, height }}
        className="color-spectrum"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      />
      {indicatorPos && (
        <div
          className="color-spectrum__indicator"
          style={{ left: indicatorPos.x, top: indicatorPos.y }}
        />
      )}
    </div>
  );
}
